import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { setDemoMode } from '../lib/store';
import { DEMO_USER } from '../lib/demo';
import type { MeDTO } from '@shared/types';

// Tre stati:
//  - 'auth'  → sessione valida (/me ok): dati reali da API.
//  - 'anon'  → backend raggiungibile ma nessuna sessione (401): mostra Login.
//  - 'demo'  → backend non raggiungibile (es. `vite dev` senza wrangler):
//              modalità dimostrativa con dataset locale.
type AuthStatus = 'loading' | 'auth' | 'anon' | 'demo';

interface AuthCtx {
  status: AuthStatus;
  user: MeDTO | null;
  refresh: () => Promise<void>;
  applyMe: (me: MeDTO) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<MeDTO | null>(null);

  // Aggiorna l'utente in cache dopo un PATCH (senza refetch).
  const applyMe = useCallback((me: MeDTO) => setUser(me), []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      setStatus('auth');
      setDemoMode(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // Backend OK ma senza sessione → login.
        setUser(null);
        setStatus('anon');
        setDemoMode(false);
      } else {
        // Rete/proxy non disponibili → demo.
        setUser(DEMO_USER);
        setStatus('demo');
        setDemoMode(true);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.login(email, password);
      setDemoMode(false);
      await refresh(); // carica il profilo completo (MeDTO)
    },
    [refresh],
  );

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      await api.register(email, password, displayName);
      setDemoMode(false);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignora */
    }
    setUser(null);
    setStatus('anon');
  }, []);

  return (
    <Ctx.Provider value={{ status, user, refresh, applyMe, login, register, logout }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth deve essere usato dentro <AuthProvider>');
  return ctx;
}
